import { useState } from "react";
import { Check, Headphones, NotebookPen, Sparkles, Layers } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import ProductModal from "../components/ProductModal";
import CheckoutModal from "../components/CheckoutModal";
import { PRODUCTS } from "../data/products";
import { IMAGES } from "../data/content";

const KIND_ICONS = { audio: Headphones, journal: NotebookPen, deck: Layers, default: Sparkles };
const PRODUCT_IMAGES = ["journalFlatlay", "headphones", "audioPerson", "journalFlatlay"];

const iconFor = (kind = "") => {
  const k = kind.toLowerCase();
  if (k.includes("audio")) return KIND_ICONS.audio;
  if (k.includes("journal")) return KIND_ICONS.journal;
  if (k.includes("deck")) return KIND_ICONS.deck;
  return KIND_ICONS.default;
};

export default function Store() {
  const [preview, setPreview] = useState(null);
  const [buying, setBuying] = useState(null);

  return (
    <div>
      <PageHero
        eyebrow="Digital Products Store"
        lines={["Small tools,", "kept forever."]}
        sub="Guided workbooks, audio companions and pocket decks — born in real sessions, yours the moment you check out. Every download is yours for keeps."
        image={IMAGES.headphones}
        alt="Minimal headphones on a warm surface"
      />

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="grid gap-6 md:grid-cols-2" data-testid="store-grid">
          {PRODUCTS.map((p, i) => {
            const Icon = iconFor(p.kind);
            return (
              <Reveal key={p.slug} delay={(i % 2) * 0.08}>
                <div className="card-soft flex h-full flex-col overflow-hidden" data-testid={`product-card-${i}`}>
                  <div className="relative overflow-hidden">
                    <img
                      src={IMAGES[PRODUCT_IMAGES[i % PRODUCT_IMAGES.length]]}
                      alt={p.name}
                      loading="lazy"
                      className="h-56 w-full object-cover"
                    />
                    <span className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-cream/90 text-pine backdrop-blur">
                      <Icon className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-8">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-sage">
                      {p.kind}
                    </p>
                    <h2 className="mt-2 font-serif text-3xl text-ink">{p.name}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-bodytext">
                      {p.description}
                    </p>
                    <ul className="mt-5 space-y-2">
                      {p.inside.slice(0, 3).map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm text-bodytext/90">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-7 flex items-center justify-between gap-3 border-t border-pine/10 pt-6">
                      <p className="font-serif text-3xl text-ink">
                        ${p.price.toFixed(0)}
                        <span className="ml-2 font-sans text-xs text-bodytext/60">USD</span>
                      </p>
                      <div className="flex gap-2.5">
                        <button
                          data-testid={`product-preview-button-${i}`}
                          onClick={() => setPreview(p)}
                          className="rounded-full border border-pine/25 px-5 py-2.5 text-xs font-semibold text-pine transition-all hover:bg-sagelight/60"
                        >
                          Preview
                        </button>
                        <button
                          data-testid={`product-buy-button-${i}`}
                          onClick={() => setBuying(p)}
                          className="rounded-full bg-pine px-5 py-2.5 text-xs font-semibold text-cream transition-all hover:-translate-y-0.5 hover:bg-pinedeep"
                        >
                          Get it now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs italic text-bodytext/60">
          Instant download after checkout · 14-day goodwill refund · files never expire
        </p>
      </section>

      <ProductModal
        product={preview}
        onClose={() => setPreview(null)}
        onBuy={(p) => {
          setPreview(null);
          setBuying(p);
        }}
      />
      <CheckoutModal product={buying} onClose={() => setBuying(null)} />
    </div>
  );
}
